import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Tooltip, TooltipContent, TooltipTrigger } from '@xiltrion/ui/tooltip';
import type { CaseDetail, DocumentRow } from '@/api/types';
import { DueBadge, StatusTag, fmtDate } from '@/components/ui';
import { CitizenLink } from '@/features/citizens/CitizenPage';
import { ImportantIcon } from '@/features/citizens/ImportantMark';
import { DocumentViewer } from './detail/DocumentViewer';
import { FavoriteStar } from './FavoriteStar';
import { FINISHED_STATUSES, finishedAt } from './lib';

/**
 * Cabecera del expediente: la misma en el expediente y en cada una de sus tareas, para tener a la vista lo esencial
 * (radicado, estado, prioridad, tipo, área, asunto, peticionario, documento del radicado, ingreso y vencimiento)
 * sin buscarlo en otra pantalla. `linkToCase`: en una tarea, el radicado lleva al expediente.
 */
export function CaseHeader({ c, actions, linkToCase, current }: { c: CaseDetail; actions?: ReactNode; linkToCase?: boolean; current?: string }) {
  const [viewing, setViewing] = useState<DocumentRow | null>(null);
  // Al bajar, cuando la cabecera sale de la vista, queda arriba una versión compacta con lo esencial.
  const ref = useRef<HTMLElement>(null);
  const [scrolledPast, setScrolledPast] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setScrolledPast(!!e && !e.isIntersecting && e.boundingClientRect.top < 100), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const petition = c.documents.find((d) => d.kind === 'PETITION') ?? null;
  /** El archivo que el ciudadano subió como petición (`isPetition`), aparte de la constancia que genera el radicado. */
  const citizenPetitions = c.documents.filter((d) => d.isPetition && !d.replacedAt);
  const finished = FINISHED_STATUSES.includes(c.status) ? finishedAt(c) : null;
  const radication = c.radication ?? <span className="text-muted-foreground">Radicado pendiente…</span>;
  return (<>
    <CompactBar c={c} visible={scrolledPast} current={current} petition={petition} onView={setViewing} finished={finished} />
    <header ref={ref} className="bg-card border rounded-lg p-4 flex flex-col gap-3" style={{ boxShadow: 'var(--elev-1)' }}>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            {linkToCase
              ? <Link to={`/pqrs/${c.id}`} className="text-lg font-semibold text-foreground hover:text-primary no-underline" title="Abrir el expediente">{radication}</Link>
              : <h1 className="text-xl font-semibold m-0">{radication}</h1>}
            <FavoriteStar caseId={c.id} label={c.radication ?? 'este expediente'} size="icon" />
            <StatusTag status={c.status} />
            {/* Junto al estado: cuánto falta para el vencimiento (cuenta regresiva). */}
            {!finished && c.dueAt && <DueBadge due={c.due} dueAt={c.dueAt} />}
            {c.requiresReview && <Badge className="bg-warning/20 text-foreground gap-1"><Icono nombre="mdi:alert" aria-hidden /> Requiere revisión</Badge>}
            {c.legalHold && <Badge variant="destructive">Retención legal</Badge>}
            {c.failedEmails > 0 && <Link to={`/pqrs/${c.id}?tab=comunicaciones`} className="no-underline" title="Ver en Comunicaciones"><Badge variant="destructive" className="gap-1"><Icono nombre="mdi:email-alert-outline" aria-hidden /> {c.failedEmails === 1 ? 'Correo no enviado' : `${c.failedEmails} correos no enviados`}</Badge></Link>}
          </div>
          <div className="text-muted-foreground mt-1">{c.caseType?.name ?? '—'} — {c.organizationalUnit?.name ?? 'Área por definir'}{c.specialty ? ` / ${c.specialty.name}` : ''}</div>
          <div className="mt-1 font-medium truncate" title={c.subject}>{c.subject}</div>
        </div>
        {actions && <div className="flex flex-col items-end gap-2">{actions}</div>}
      </div>
      <dl className="m-0 grid gap-x-6 gap-y-2 border-t border-border pt-3 text-sm grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <Fact icon="mdi:account-outline" label="Peticionario">
          {c.anonymous ? <span className="text-muted-foreground">Anónimo</span> : c.citizen ? <span className="inline-flex items-center gap-1"><CitizenLink id={c.citizen.id} name={c.citizen.fullName} />{c.citizen.important && <ImportantIcon />}</span> : '—'}
        </Fact>
        {/* El documento que genera el radicado (primario) y el que subió el ciudadano como petición (verde), uno al lado del otro. */}
        <Fact icon="mdi:file-document-outline" label="Petición">
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            {petition
              ? <DocButton icon="mdi:file-certificate" tone="text-primary" label={`Documento del radicado: ${petition.documentType ?? 'Petición'}${petition.indexOrder ? ` (n.º ${petition.indexOrder})` : ''}`} onClick={() => setViewing(petition)} />
              : <span className="text-muted-foreground">Aún no generado</span>}
            {citizenPetitions.map((d) => <DocButton key={d.id} icon="mdi:file-account" tone="text-success" label={`Petición que subió el ciudadano: ${d.fileName}`} onClick={() => setViewing(d)} />)}
          </span>
        </Fact>
        <Fact icon="mdi:calendar-import" label="Fecha de ingreso"><span className="tnum">{fmtDate(c.radicatedAt ?? c.createdAt)}</span></Fact>
        {finished
          ? <Fact icon="mdi:calendar-check" label="Fecha de cierre"><span className="tnum">{fmtDate(finished)}</span></Fact>
          : <Fact icon="mdi:calendar-clock" label="Vencimiento">{c.dueAt ? <span className="tnum">{fmtDate(c.dueAt)}</span> : <span className="text-muted-foreground">Sin término</span>}</Fact>}
      </dl>
      {viewing && <DocumentViewer doc={viewing} onHide={() => setViewing(null)} />}
    </header>
  </>);
}

/**
 * La cabecera en una línea, fija arriba del área de contenido mientras la grande no se ve (`-top-*` = el padding de
 * `main`: así se pega al borde y no deja un hueco encima). Ocupa altura cero en la
 * página (la barra va encima), así que aparecer o desaparecer no mueve el contenido.
 */
function CompactBar({ c, visible, current, petition, onView, finished }: { c: CaseDetail; visible: boolean; current?: string; petition: DocumentRow | null; onView: (d: DocumentRow) => void; finished: string | null }) {
  return (
    <div className="sticky -top-4 lg:-top-6 z-30 h-0 -mb-4" aria-hidden={!visible}>
      <div className={`-mx-4 lg:-mx-6 bg-card/95 backdrop-blur border-b border-border shadow-sm px-4 lg:px-6 py-2 flex items-center gap-x-4 gap-y-1 flex-wrap text-sm transition-all duration-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
        <Link to={`/pqrs/${c.id}`} tabIndex={visible ? 0 : -1} className="font-semibold text-foreground hover:text-primary no-underline">{c.radication ?? 'Radicado pendiente'}</Link>
        <StatusTag status={c.status} />
        {current && <span className="font-medium truncate max-w-56" title={current}>{current}</span>}
        <span className="text-muted-foreground truncate max-w-64" title={c.subject}>{c.subject}</span>
        <span className="inline-flex items-center gap-1 min-w-0"><Icono nombre="mdi:account-outline" className="text-muted-foreground" aria-hidden /><span className="truncate max-w-48">{c.anonymous ? 'Anónimo' : c.citizen?.fullName ?? '—'}</span></span>
        {petition && <Button type="button" variant="link" size="sm" tabIndex={visible ? 0 : -1} className="gap-1" onClick={() => onView(petition)}><Icono nombre="mdi:file-pdf-box" aria-hidden /> Petición</Button>}
        <span className="ml-auto inline-flex items-center gap-2 text-muted-foreground tnum">
          <span>Ingreso {fmtDate(c.radicatedAt ?? c.createdAt, false)}</span>
          {finished ? <span>· Cierre {fmtDate(finished, false)}</span> : c.dueAt ? <><span>· Vence {fmtDate(c.dueAt, false)}</span><DueBadge due={c.due} dueAt={c.dueAt} /></> : null}
        </span>
      </div>
    </div>
  );
}

/** Un documento de la cabecera como solo un ícono, sin marco de botón: el nombre va en el tooltip. */
function DocButton({ icon, tone, label, onClick }: { icon: NombreIcono; tone: string; label: string; onClick: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild><button type="button" aria-label={label} onClick={onClick} className={`inline-flex rounded-sm transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tone}`}><Icono nombre={icon} className="size-6" /></button></TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-72">{label}</TooltipContent>
    </Tooltip>
  );
}

function Fact({ icon, label, children }: { icon: NombreIcono; label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground flex items-center gap-1"><Icono nombre={icon} aria-hidden /> {label}</dt>
      <dd className="m-0 mt-0.5 truncate">{children}</dd>
    </div>
  );
}
